// Angular
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
// Material
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
  MatDialogConfig,
} from '@angular/material/dialog';
// RxJS
import { skip } from 'rxjs/operators';

import {
  ContactRelationshipAgreementsDataSource,
  ContactRelationshipAgreementsPageRequested,
  ClearContactRelationshipAgreementState,
  ContactRelationshipAgreementModel,
  selectContactRelationshipAgreementsActionLoading,
} from '../../../../../../../../core/lists';

import { SelectionModel } from '@angular/cdk/collections';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../../core/reducers';
import { Agreement } from 'src/app/core/agreement/_models/agreement.model';
import { AgreementService } from 'src/app/core/agreement/_services/agreements.service';
import { PdfViewDialogComponent } from 'src/app/views/pages/agreement-management/agreement/pdf-view/pdf-view';
import {
  AgreementActionTypes,
  AgreementReminderRequested,
  RevokeAgreementRequested,
} from 'src/app/core/agreement/_actions/agreement.actions';
import { ConfirmRevokeDialogComponent } from 'src/app/views/pages/agreement-management/agreement/agreement-list/confirm-revoke-dialog/confirm-revoke-dialog.component';
import {
  LayoutUtilsService,
  MessageType,
} from 'src/app/core/_base/crud/utils/layout-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { selectAgreementLatestSuccessfullAction } from 'src/app/core/agreement/_selectors/agreement.selectors';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-relationship-agreements-dialog',
  templateUrl: './relationship-agreements-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelationshipAgreementsDialogComponent implements OnInit, OnDestroy {
  // Public properties
  loading = true;
  loadingAfterSubmit = false;
  viewLoading = false;
  actionLoading = false;
  dataSource: ContactRelationshipAgreementsDataSource;
  contactRelationshipsResult: ContactRelationshipAgreementModel[] = [];
  displayedColumns = ['id', 'status', 'date_sent', 'void_date', 'void_reason', 'actions'];
  selection = new SelectionModel<ContactRelationshipAgreementModel>(true, []);
  subscriptions: Subscription[] = [];

  /**
   * Component constructor
   *
   * @param dialogRef: MatDialogRef<PatientRelationshipEditComponent>
   * @param data: any
   */
  constructor(
    public dialogRef: MatDialogRef<RelationshipAgreementsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private agreementService: AgreementService,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private store: Store<AppState>,
  ) {}

  /**
   * On init
   */
  ngOnInit() {
    this.subscriptions.push(
      this.store
        .pipe(select(selectContactRelationshipAgreementsActionLoading))
        .subscribe((res) => (this.viewLoading = res)),
    );

    this.loadAgreementList();

    // Init DataSource
    this.dataSource = new ContactRelationshipAgreementsDataSource(this.store);

    this.subscriptions.push(
      this.store.pipe(select(selectAgreementLatestSuccessfullAction), skip(1)).subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case AgreementActionTypes.AgreementReminded:
              successMsg = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.MESSAGE');
              msgType = MessageType.Update;
              this.loadAgreementList();
              break;
            case AgreementActionTypes.AgreementRevoked:
              successMsg = this.translate.instant('AGREEMENT.REVOKE_AGREEMENT.MESSAGE');
              msgType = MessageType.Update;
              this.loadAgreementList();
              break;
          }

          this.actionLoading = false;

          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
        }
      }),
    );
  }

  ngOnDestroy() {
    this.store.dispatch(new ClearContactRelationshipAgreementState());
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Close dialog
   */
  onNoClick(): void {
    this.dialogRef.close({ isUpdated: false });
  }

  /**
   * Close dialog
   *
   * @param specId: any
   */
  closeDialog(response) {
    this.dialogRef.close({
      done: true,
      response,
    });
  }

  getTitle(): string {
    return 'CONTACT_RELATIONSHIP.VIEW_CONTACT_AGREEMENTS';
  }

  loadAgreementList() {
    this.selection.clear();

    // Call request from server
    this.store.dispatch(
      new ContactRelationshipAgreementsPageRequested({
        contactId: this.data.contactId,
        entityId: this.data.entityId,
      }),
    );
  }

  revokeAgreementButtonOnClick(agreement) {
    const dialogRef = this.dialog.open(ConfirmRevokeDialogComponent, {
      width: '450px',
      disableClose: true,
      closeOnNavigation: true,
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.actionLoading = true;
      this.store.dispatch(new RevokeAgreementRequested({ agreement, reason: res.reason }));
    });
  }

  remindAgreementButtonOnClick(agreement) {
    const title: string = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.TITLE');
    const description: string = this.translate.instant('AGREEMENT.REMIND_AGREEMENT.DESCRIPTION');
    const waitDesciption: string = this.translate.instant(
      'AGREEMENT.REMIND_AGREEMENT.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;

      this.store.dispatch(new AgreementReminderRequested({ agreement }));
    });
  }

  viewAgreementButtonOnClick(agreement: Agreement) {
    this.subscriptions.push(
      this.agreementService.getAgreement(agreement.id).subscribe((res) => {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.closeOnNavigation = true;
        dialogConfig.height = '80vh';
        dialogConfig.maxHeight = '80vh';
        dialogConfig.width = '75vh';
        dialogConfig.maxWidth = '75vh';
        dialogConfig.data = { url: res.url };

        this.dialog.open(PdfViewDialogComponent, dialogConfig);
      }),
    );
  }
}
